import { Component, input, inject, output, computed, signal, effect, HostListener, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

import { TeamLogoLookupPipe } from '../../shared/team-logo-lookup.pipe';
import { TimespanMsPipe, TimespanShortPipe } from '../../shared/time-span.pipe';

import { ISessionEntry } from '../IRaceTiming';
import { PositionChangeIndicatorComponent } from '../position-change-indicator.component';
import { TimingTowerDrawerComponent } from '../timing-tower-drawer.component';
import { RaceSessionService } from '../race-session.service';
import { TimingTowerService, ToggleAction } from '../timing-tower.service';
import { FavoriteDriverService } from '../../shared/favorite-driver.service';
import {
	TimingTowerConfig,
	TimingTowerColumnConfig,
	TIMING_TOWER_COMPACT_CONFIG,
	TimingTowerColumnId,
} from './timing-tower-column-config';

@Component({
	selector: 'vb-lxp-timing-tower-configurable',
	imports: [
		CommonModule,
		DragDropModule,
		TeamLogoLookupPipe,
		TimespanMsPipe,
		TimespanShortPipe,
		PositionChangeIndicatorComponent,
		TimingTowerDrawerComponent,
	],
	templateUrl: './timing-tower-configurable.component.html',
	styleUrl: './timing-tower-configurable.component.css',
})
export class TimingTowerConfigurableComponent {
	public config = input<TimingTowerConfig>(TIMING_TOWER_COMPACT_CONFIG);
	public Math = Math;

	public enableColumnCustomization = input(true);

	public hideColumnSelection = input(false);

	public enableMoreColumns = input(false);

	public activeClass = input('');

	public activeNumberClass = input('');

	public selectedClasses = input<string[]>([]);

	public selectionAdded = output<ISessionEntry>();

	public selectionRemoved = output<ISessionEntry>();

	public raceSessionService = inject(RaceSessionService);
	public timingTowerService = inject(TimingTowerService);
	public favoriteDriverService = inject(FavoriteDriverService);
	private elementRef = inject(ElementRef);
	private cdr = inject(ChangeDetectorRef);

	public positionChangeStateByNumber = this.raceSessionService.positionChangeStateByNumber;

	public expandedTeamNumber = signal<string | null>(null);

	public isColumnSettingsOpen = signal(false);

	public currentConfig = signal<TimingTowerConfig>(TIMING_TOWER_COMPACT_CONFIG);

		private readonly COLUMN_CONFIG_STORAGE_KEY = 'timing-tower-column-config';
	constructor() {
		effect(() => {
			const newConfig = this.config();
			const savedConfig = this.loadColumnConfigFromStorage();
			
			if (savedConfig && savedConfig.configId === newConfig.configId) {
				this.currentConfig.set({
					...newConfig,
					columns: this.mergeColumnConfigs(newConfig.columns, savedConfig.columns),
				});
			} else {
				this.currentConfig.set(newConfig);
			}
		});
	}

	public teams = computed(() => {
		const selectedClasses = this.selectedClasses();
		const entries = this.raceSessionService.raceTeamsPosition();

		return entries.filter((entry) => {
			return selectedClasses.length === 0 || selectedClasses.includes(entry.class);
		});
	});

	public visibleColumns = computed(() => {
		const config = this.currentConfig();
		const enableMore = this.enableMoreColumns();
		const gapByClass = this.raceSessionService.gapByClass || config.gapByClass;

		return config.columns.filter((col) => {
			if (!col.visible) return false;

			if (col.conditional) {
				if (col.id === 'classGap') {
					return gapByClass && enableMore;
				}
				if (col.id === 'interval') {
					return gapByClass ? true : enableMore;
				}
				return enableMore;
			}

			return true;
		});
	});

	public get drawerColspan(): number {
		const baseColspan = this.visibleColumns().length;
		const showColumnButton = this.enableColumnCustomization() && this.config().enableDrawer;
		return showColumnButton ? baseColspan + 1 : baseColspan;
	}

	public isColumnVisible(columnId: string): boolean {
		return this.visibleColumns().some((col) => col.id === columnId);
	}

	public toggleTeamSelection(team: ISessionEntry) {
		const config = this.config();

		if (config.enablePlaybackSelection && !team.playback?.url) {
			return;
		}

		const action = this.timingTowerService.toggleActive(team.number);

		if (config.enablePlaybackSelection) {
			switch (action) {
				case ToggleAction.ADDED: {
					this.selectionAdded.emit(team);
					break;
				}
				case ToggleAction.REMOVED: {
					this.selectionRemoved.emit(team);
					break;
				}
			}
		}
	}

	public toggleDrawer(team: any, event: MouseEvent): void {
		event.stopPropagation();
		const selectedNumber = team?.number ?? null;
		this.expandedTeamNumber.update((current) =>
			current === selectedNumber ? null : selectedNumber,
		);
	}

	public isDrawerOpen(team: any): boolean {
		return this.expandedTeamNumber() === team?.number;
	}

	public toggleFavoriteDriver(team: ISessionEntry): void {
		if (this.favoriteDriverService.isFavoriteDriver(team)) {
			this.favoriteDriverService.removeFavoriteDriver(team);
		} else {
			this.favoriteDriverService.addFavoriteDriver(team);
		}
	}

	public trackByFn(index: number, item: any): string {
		return item.number;
	}

	public getAlignmentClass(alignment?: string): string {
		switch (alignment) {
			case 'left':
				return 'text-left';
			case 'center':
				return 'text-center';
			case 'right':
				return 'text-right';
			default:
				return '';
		}
	}

	public openColumnSettings(): void {
		this.isColumnSettingsOpen.update(value => !value);
	}

	public closeColumnSettings(): void {
		this.isColumnSettingsOpen.set(false);
	}

	@HostListener('document:click', ['$event'])
	public onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		const isSettingsButton = target.closest('.column-settings-btn');
		
		if (isSettingsButton || !this.isColumnSettingsOpen()) {
			return;
		}
		
		const clickedInside = this.elementRef.nativeElement.contains(event.target);
		if (!clickedInside) {
			this.closeColumnSettings();
		}
	}

	public onHeaderDrop(event: CdkDragDrop<TimingTowerColumnConfig[]>): void {
		const visibleCols = this.visibleColumns();
		const allCols = [...this.currentConfig().columns];

		const prevVisibleCol = visibleCols[event.previousIndex];
		const currVisibleCol = visibleCols[event.currentIndex];

		const prevIndex = allCols.findIndex((c) => c.id === prevVisibleCol.id);
		const currIndex = allCols.findIndex((c) => c.id === currVisibleCol.id);

		if (prevIndex !== -1 && currIndex !== -1) {
			moveItemInArray(allCols, prevIndex, currIndex);
			this.currentConfig.update((config) => ({
				...config,
				columns: allCols,
			}));

			this.saveColumnConfigToStorage();
		}
	}

	public toggleColumnVisibility(columnId: string): void {
		this.currentConfig.update((config) => ({
			...config,
			columns: config.columns.map((col) =>
				col.id === columnId ? { ...col, visible: !col.visible } : col,
			),
		}));
		this.saveColumnConfigToStorage();
	}

	public resetColumns(): void {
		this.currentConfig.set(this.config());
		this.clearColumnConfigFromStorage();
		this.closeColumnSettings();
	}

	public getColumnLabel(column: TimingTowerColumnConfig): string {
		if (column.label) return column.label;

		const labelMap: Record<string, string> = {
			detailsToggle: 'Details Toggle',
			position: 'Position',
			carNumber: 'Car Number',
			carLogo: 'Car Logo',
			teamDriver: 'Team/Driver',
			classPosition: 'Class Position',
			lap: 'Lap',
			gap: 'Gap',
			classGap: 'Class Gap',
			interval: 'Interval',
			lastLap: 'Last Lap',
			bestLap: 'Best Lap',
			sector1: 'Sector 1',
			sector2: 'Sector 2',
			sector3: 'Sector 3',
			pits: 'Pit Stops',
			maxSpeed: 'Max Speed',
		};

		return labelMap[column.id] || column.id;
	}

	private saveColumnConfigToStorage(): void {
		try {
			const config = this.currentConfig();
			const storageData = {
				configId: config.configId,
				columns: config.columns.map((col) => ({
					id: col.id,
					visible: col.visible,
				})),
			};
			sessionStorage.setItem(this.COLUMN_CONFIG_STORAGE_KEY, JSON.stringify(storageData));
		} catch (error) {
			console.error('Failed to save column configuration to session storage:', error);
		}
	}

	private loadColumnConfigFromStorage(): { configId: string; columns: Array<{ id: TimingTowerColumnId; visible: boolean }> } | null {
		try {
			const stored = sessionStorage.getItem(this.COLUMN_CONFIG_STORAGE_KEY);
			if (stored) {
				return JSON.parse(stored);
			}
		} catch (error) {
			console.error('Failed to load column configuration from session storage:', error);
		}
		return null;
	}

	private clearColumnConfigFromStorage(): void {
		try {
			sessionStorage.removeItem(this.COLUMN_CONFIG_STORAGE_KEY);
		} catch (error) {
			console.error('Failed to clear column configuration from session storage:', error);
		}
	}

	private mergeColumnConfigs(
		defaultColumns: TimingTowerColumnConfig[],
		savedColumns: Array<{ id: TimingTowerColumnId; visible: boolean }>,
	): TimingTowerColumnConfig[] {
		const savedMap = new Map(savedColumns.map((col) => [col.id, col]));

		const defaultMap = new Map(defaultColumns.map((col) => [col.id, col]));

		const orderedColumns: TimingTowerColumnConfig[] = [];
		const processedIds = new Set<string>();

		for (const savedCol of savedColumns) {
			const defaultCol = defaultMap.get(savedCol.id);
			if (defaultCol) {
				orderedColumns.push({
					...defaultCol,
					visible: savedCol.visible,
				});
				processedIds.add(savedCol.id);
			}
		}

		for (const defaultCol of defaultColumns) {
			if (!processedIds.has(defaultCol.id)) {
				orderedColumns.push(defaultCol);
			}
		}

		return orderedColumns;
	}
}
